import { Routes, Route, useLocation, useNavigate } from "react-router";
import { Suspense, useEffect, useLayoutEffect, useRef } from "react";

import "./App.css";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { useAuth } from "./hooks/useAuth";
import { useFeatures } from "./hooks/useFeatures";
import TermsAndWizardGuard from "./components/layout/TermsAndWizardGuard";
import { withProviderOnlyGuard } from "./utils/withProviderOnlyGuard";
import { withPeptidesGuard } from "./utils/withPeptidesGuard";
import { withoutNonAdminSalesRepGuard } from "./utils/withoutNonAdminSalesRepGuard";
import { computeHomeRoute } from "./utils/computeHomeRoute";

// Page-level components are code-split so a session only downloads the module
// it actually visits. First-paint cost drops by every page the user doesn't open.
const Home = lazyWithRetry(() => import("./pages/Home"));
const Login = lazyWithRetry(() => import("./pages/Login"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const ChangePassword = lazyWithRetry(() => import("./pages/ChangePassword"));
const CreateAccount = lazyWithRetry(() => import("./pages/CreateAccount"));
const UserManagement = lazyWithRetry(() => import("./pages/UserManagement"));
const UserEdit = lazyWithRetry(() => import("./pages/UserEdit"));
const Analytics = lazyWithRetry(() => import("./pages/Analytics"));
const Orders = lazyWithRetry(() => import("./pages/Orders"));
const TableViewPage = lazyWithRetry(() => import("./pages/TableViewPage"));
const LocationsList = lazyWithRetry(() => import("./pages/LocationsList"));
const LocationsEdit = lazyWithRetry(() => import("./pages/LocationsEdit"));
const EHRTemplates = lazyWithRetry(() => import("./pages/EHRTemplates"));
const EHRTemplatesEdit = lazyWithRetry(() => import("./pages/EHRTemplatesEdit"));
const SalesDashboard = lazyWithRetry(() => import("./pages/SalesDashboard"));
const ProofOfDeliveryPortal = lazyWithRetry(() => import("./components/orders/ProofOfDeliveryPortal"));
const VoiceEncounterPortal = lazyWithRetry(() => import("./components/AIAgent/VoiceEncounterPortal"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Patients = lazyWithRetry(() => import("./pages/Patients"));
const PatientsEdit = lazyWithRetry(() => import("./pages/PatientsEdit"));
const ClinicalProfileDetail = lazyWithRetry(() => import("./pages/ClinicalProfileDetail"));
const Onboarding = lazyWithRetry(() => import("./pages/Onboarding"));
const DMECredentialing = lazyWithRetry(() => import("./pages/DMECredentialing"));
const AIAgent = lazyWithRetry(() => import("./pages/AIAgent"));
const Peptides = lazyWithRetry(() => import("./pages/Peptides"));
const PatientIntakePortal = lazyWithRetry(() => import("./components/peptides/PatientIntakePortal"));
const ClinicalProfilePortal = lazyWithRetry(() => import("./components/peptides/ClinicalProfilePortal"));
const GuardedMyClinicalProfile = lazyWithRetry(() => import("./pages/GuardedMyClinicalProfile"));
const StackBuilderPortal = lazyWithRetry(() => import("./components/peptides/StackBuilderPortal"));
const AIAgentDashboard = lazyWithRetry(() => import("./pages/AIAgentDashboard"));
const MyPlan = lazyWithRetry(() => import("./pages/MyPlan"));
const SessionQueue = lazyWithRetry(() => import("./pages/SessionQueue"));
const AdminTemplates = lazyWithRetry(() => import("./pages/AdminTemplates"));
// Chiropractic sub-app — fully self-contained shell, code-split like the rest.
const ChiroApp = lazyWithRetry(() => import("./chiro/ChiroApp"));

function withFeatureGuard(feature, Component) {
  return function (props) {
    const { auth } = useAuth();
    // Use `hasFeature` (silent check), not `canUseFeature` — the latter pops
    // the upgrade-required modal, which is wrong for a route guard and was
    // surfacing an empty "Pro Feature" modal for new DME providers when the
    // auth payload arrived in stages and the guard saw partial state.
    const { hasFeature } = useFeatures();
    const navigate = useNavigate();

    useEffect(() => {
      // Wait for both session and orgInfo before deciding. New orgs hydrate
      // these separately, so checking too early can wrongly redirect.
      if (!auth?.session || !auth?.orgInfo) return;
      if (!hasFeature(feature)) {
        navigate("/");
      }
    }, [auth?.session, auth?.orgInfo, hasFeature, navigate]);

    return <Component {...props} />;
  };
}

function withGroupsGuard(groups, Component) {
  return function (props) {
    const { auth, hasGroup } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      if (!auth?.user) return;
      const hasRequiredGroup = groups.some((group) => hasGroup(group));
      if (!hasRequiredGroup) {
        navigate("/");
      }
    }, [auth?.user, hasGroup, navigate]);

    return <Component {...props} />;
  };
}

const GuardedUserManagement = withFeatureGuard("team", withGroupsGuard(["supervisor"], UserManagement));
const GuardedMyPlan = withFeatureGuard("encounters", withGroupsGuard(["supervisor"], MyPlan));
const GuardedUserEdit = withFeatureGuard("team", UserEdit);
const GuardedLocationsList = withFeatureGuard("locations", LocationsList);
const GuardedLocationsEdit = withFeatureGuard("locations", LocationsEdit);
const GuardedAnalytics = withFeatureGuard("analytics", Analytics);
const GuardedTableViewPage = withFeatureGuard("managementGrid", TableViewPage);
const GuardedOrders = withFeatureGuard("orders", Orders);
const GuardedOrdersManagement = withFeatureGuard("ordersManagement", Orders);
const GuardedOnboarding = withFeatureGuard("ordersManagement", Onboarding);
const GuardedDMECredentialing = withFeatureGuard("ordersManagement", DMECredentialing);
const GuardedSessionQueue = withFeatureGuard("encounters", SessionQueue);
const GuardedAIAgent = withFeatureGuard("encounters", AIAgent);
const GuardedAIAgentDashboard = withFeatureGuard("encounters", AIAgentDashboard);
// Form Editor is manufacturer-only. Providers see the provider-side NAF
// signing flow, not this editor. Gate on the ordersManagement feature
// (which maps to the order-manufacturer plan) plus supervisor group.
const GuardedFormEditor = withFeatureGuard("ordersManagement", withGroupsGuard(["supervisor"], AdminTemplates));

// Chiropractors live entirely inside the ChiroApp shell. Any non-chiro user
// who somehow lands on a chiro-resolved route is bounced back to "/", where
// computeHomeRoute sends them to their correct module.
function withChiroGuard(Component) {
  return function (props) {
    const { auth } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
      if (!auth?.user) return;
      if (auth?.user?.custom?.doctorType !== "chiropractor") {
        navigate("/");
      }
    }, [auth?.user, navigate]);

    return <Component {...props} />;
  };
}

const GuardedPeptides = withoutNonAdminSalesRepGuard("/salesDashboard", withPeptidesGuard(Peptides));
const ProviderOnlyPatients = withProviderOnlyGuard(Patients);
const ProviderOnlyPatientsEdit = withProviderOnlyGuard(PatientsEdit);
const ProviderOnlyClinicalProfileDetail = withProviderOnlyGuard(ClinicalProfileDetail);
const GuardedSalesDashboard = withFeatureGuard(
  "ordersSalesDashboard",
  withGroupsGuard(["sales-rep", "sales-rep-master", "supervisor"], SalesDashboard)
);

const GuardedChiroApp = withChiroGuard(ChiroApp);

const HomeComponent = {
  default: Home,
  salesRep: GuardedSalesDashboard,
  ordersManagement: GuardedOrdersManagement,
  orders: GuardedOrders,
  peptides: GuardedPeptides,
  chiro: GuardedChiroApp,
};

function RoutesComponent() {
  const { auth, checkAutoLogin, home, setHome } = useAuth();
  const { hasFeature } = useFeatures();
  const location = useLocation();
  const lastPathRef = useRef(null);

  // Scroll to top if path changes
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (lastPathRef.current) {
      sessionStorage.setItem("trainingRedirectPreviousPath", lastPathRef.current);
    } else {
      sessionStorage.removeItem("trainingRedirectPreviousPath");
    }
    sessionStorage.setItem("trainingRedirectCurrentPath", location.pathname);
    lastPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    checkAutoLogin();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const next = computeHomeRoute(auth, { hasFeature });
    if (next !== null) {
      setHome(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const homeComponent = HomeComponent[home] || HomeComponent.default;
  // The encounter-detail route normally renders Home (which hosts ChatDetail).
  // Chiropractors view encounters inside their own shell, so divert just them;
  // every other module keeps the original hardcoded Home behavior.
  const chatComponent = home === "chiro" ? GuardedChiroApp : Home;

  return (
    <TermsAndWizardGuard>
      <Suspense fallback={<div className="route-suspense-fallback" aria-busy="true" />}>
        <Routes>
          {auth ? (
            <>
              <Route path="/" exact Component={homeComponent} />
              <Route path="/table" exact Component={GuardedTableViewPage} />
              <Route path="/chat/:chatId" exact Component={chatComponent} />
              <Route path="/ehrTemplates" exact Component={EHRTemplates} />
              <Route path="/ehrTemplates/:templateId" exact Component={EHRTemplatesEdit} />
              <Route path="/users" exact Component={GuardedUserManagement} />
              <Route path="/users/:userId" exact Component={GuardedUserEdit} />
              <Route path="/my-plan" exact Component={GuardedMyPlan} />
              <Route path="/locations" exact Component={GuardedLocationsList} />
              <Route path="/locations/:locationId" exact Component={GuardedLocationsEdit} />
              <Route path="/analytics" exact Component={GuardedAnalytics} />
              <Route path="/settings/:settingId" exact Component={Settings} />
              <Route path="/patients/:patientId/clinical-profile" exact Component={ProviderOnlyClinicalProfileDetail} />
              <Route path="/patients/:patientId" exact Component={ProviderOnlyPatientsEdit} />
              <Route path="/patients" exact Component={ProviderOnlyPatients} />
              <Route path="/orders" exact Component={GuardedOrders} />
              <Route path="/onboarding" exact Component={GuardedOnboarding} />
              <Route path="/dme-credentialing" exact Component={GuardedDMECredentialing} />
              <Route path="/ordersManagement" exact Component={GuardedOrdersManagement} />
              <Route path="/salesDashboard" exact Component={GuardedSalesDashboard} />
              <Route path="/admin/form-editor" exact Component={GuardedFormEditor} />
              <Route path="/session-queue" exact Component={GuardedSessionQueue} />
              <Route path="/ai-agent/settings" exact Component={GuardedAIAgentDashboard} />
              <Route path="/ai-agent" exact Component={GuardedAIAgent} />
              <Route path="/peptides" exact Component={GuardedPeptides} />
              <Route path="/orderDelivered/:orderId" element={<ProofOfDeliveryPortal />} />
              <Route path="/clinical-profile" element={<ClinicalProfilePortal />} />
              <Route path="/my-profile" element={<GuardedMyClinicalProfile />} />
              <Route path="/patient-intake" element={<PatientIntakePortal />} />
              <Route path="/stack-builder" element={<StackBuilderPortal />} />
              <Route path="/voice-encounter" element={<VoiceEncounterPortal />} />
              <Route path="/*" Component={homeComponent}></Route>
            </>
          ) : (
            <>
              <Route path="/resetPassword" element={<ResetPassword />} />
              <Route path="/changePassword" element={<ChangePassword />} />
              <Route path="/createAccount" element={<CreateAccount />} />
              <Route path="/orderDelivered/:orderId" element={<ProofOfDeliveryPortal />} />
              <Route path="/clinical-profile" element={<ClinicalProfilePortal />} />
              <Route path="/patient-intake" element={<PatientIntakePortal />} />
              <Route path="/stack-builder" element={<StackBuilderPortal />} />
              <Route path="/voice-encounter" element={<VoiceEncounterPortal />} />

              <Route path="/*" element={<Login />} />
            </>
          )}
        </Routes>
      </Suspense>
    </TermsAndWizardGuard>
  );
}

export default RoutesComponent;
